const AUDIO_OUTPUT_STORAGE_KEY = "searchjobai.liveInterview.audioOutputDeviceId";

export function supportsAudioOutputSelection() {
  if (typeof HTMLMediaElement === "undefined") return false;
  return typeof HTMLMediaElement.prototype.setSinkId === "function";
}

export function readStoredAudioOutputDeviceId() {
  if (typeof window === "undefined") return "";
  try {
    return String(window.localStorage.getItem(AUDIO_OUTPUT_STORAGE_KEY) || "").trim();
  } catch {
    return "";
  }
}

export function persistAudioOutputDeviceId(deviceId) {
  if (typeof window === "undefined") return;
  const id = String(deviceId || "").trim();
  try {
    if (!id) {
      window.localStorage.removeItem(AUDIO_OUTPUT_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(AUDIO_OUTPUT_STORAGE_KEY, id);
  } catch {
    // Best-effort only.
  }
}

export function resolveAudioOutputDeviceId(devices = [], preferredId = "") {
  const list = Array.isArray(devices) ? devices : [];
  const preferred = String(preferredId || "").trim();
  if (preferred && list.some((device) => String(device?.deviceId || "") === preferred)) {
    return preferred;
  }
  const stored = readStoredAudioOutputDeviceId();
  if (stored && list.some((device) => String(device?.deviceId || "") === stored)) {
    return stored;
  }
  const named = list.find((device) => {
    const id = String(device?.deviceId || "").trim();
    return id && id !== "default" && id !== "communications";
  });
  if (named?.deviceId) return String(named.deviceId);
  return String(list[0]?.deviceId || "").trim();
}

async function applySinkIdToElement(element, deviceId) {
  const id = String(deviceId || "").trim();
  if (!element || typeof element.setSinkId !== "function" || !id) return false;
  await element.setSinkId(id);
  return true;
}

export async function applyAudioOutputDevice(deviceId, mediaElement) {
  const id = String(deviceId || "").trim();
  if (!id) return false;
  try {
    if (mediaElement) {
      return await applySinkIdToElement(mediaElement, id);
    }
  } catch (error) {
    console.warn("[LiveInterview] Could not set speaker on media element", error);
  }
  return false;
}

async function applySinkIdToPageMedia(deviceId) {
  if (typeof document === "undefined") return false;
  const mediaNodes = document.querySelectorAll("audio, video");
  let applied = false;
  await Promise.all(
    Array.from(mediaNodes).map(async (node) => {
      try {
        const ok = await applySinkIdToElement(node, deviceId);
        if (ok) applied = true;
      } catch {
        // Some browsers reject setSinkId on individual elements.
      }
    })
  );
  return applied;
}

export async function applyAudioOutputToLiveKitRoom(room, deviceId) {
  const id = String(deviceId || "").trim();
  if (!id) return false;
  let switched = false;
  if (room) {
    try {
      if (typeof room.setSinkId === "function") {
        await room.setSinkId(id);
        switched = true;
      } else if (typeof room.switchActiveDevice === "function") {
        await room.switchActiveDevice("audiooutput", id);
        switched = true;
      }
    } catch (error) {
      console.warn("[LiveInterview] Could not switch LiveKit speaker", error);
    }
  }
  const appliedToElements = await applySinkIdToPageMedia(id);
  return switched || appliedToElements;
}

function writeWavString(view, offset, value) {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function createBeepWavUrl(durationMs = 420, frequency = 880) {
  const sampleRate = 44100;
  const sampleCount = Math.max(1, Math.floor((sampleRate * durationMs) / 1000));
  const dataSize = sampleCount * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  writeWavString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeWavString(view, 8, "WAVE");
  writeWavString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeWavString(view, 36, "data");
  view.setUint32(40, dataSize, true);
  for (let index = 0; index < sampleCount; index += 1) {
    const time = index / sampleRate;
    const attack = Math.min(1, index / 600);
    const release = Math.min(1, (sampleCount - index) / 1800);
    const sample = Math.round(
      Math.sin(2 * Math.PI * frequency * time) * attack * release * 0.28 * 32767
    );
    view.setInt16(44 + index * 2, sample, true);
  }
  const blob = new Blob([buffer], { type: "audio/wav" });
  return URL.createObjectURL(blob);
}

export async function playTestSpeakerSound(deviceId = "") {
  const id = String(deviceId || "").trim();
  if (typeof Audio !== "undefined") {
    const objectUrl = createBeepWavUrl();
    const audio = new Audio(objectUrl);
    try {
      if (id && typeof audio.setSinkId === "function") {
        await audio.setSinkId(id);
      }
      await audio.play();
      await new Promise((resolve) => window.setTimeout(resolve, 480));
      URL.revokeObjectURL(objectUrl);
      return true;
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      console.warn("[LiveInterview] Speaker test playback failed", error);
    }
  }

  const AudioContextCtor =
    typeof window !== "undefined"
      ? window.AudioContext || window.webkitAudioContext
      : null;
  if (!AudioContextCtor) return false;
  const ctx = new AudioContextCtor();
  try {
    if (id && typeof ctx.setSinkId === "function") {
      await ctx.setSinkId(id);
    }
    await ctx.resume?.();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
    await new Promise((resolve) => window.setTimeout(resolve, 520));
    await ctx.close?.();
    return true;
  } catch (error) {
    console.warn("[LiveInterview] WebAudio speaker test failed", error);
    try {
      await ctx.close?.();
    } catch {
      // ignore
    }
    return false;
  }
}
